import { create } from 'zustand';

/**
 * Applied operator overrides to enriched field values, mirrored client-side.
 *
 * Persistence is per-cell: each edit is PATCHed to the shared catalog-ops
 * attribute endpoint the moment it is committed (see `use-update-enriched-attribute.ts`).
 * This store is only the local mirror of a *succeeded* save, so the edited cell
 * reflects the new value immediately without refetching the whole product list.
 * A save that fails never reaches here — nothing is stored optimistically.
 */

/** Composite key so one store can hold overrides for every experiment/workflow the operator touches. */
function editKey(scopeId: string, productId: string, fieldKey: string): string {
  return `${scopeId}::${productId}::${fieldKey}`;
}

/** Prefix test used to scope bulk operations to one run. */
function isInScope(key: string, scopeId: string): boolean {
  return key.startsWith(`${scopeId}::`);
}

/** Returns a copy of `map` with every key belonging to `scopeId` removed. */
function withoutScope(map: Record<string, unknown>, scopeId: string): Record<string, unknown> {
  const next: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(map)) {
    if (!isInScope(key, scopeId)) next[key] = value;
  }
  return next;
}

/** Returns a copy of `map` without one key — rebuilt rather than `delete`d, which the lint config forbids on computed keys. */
function withoutKey(map: Record<string, unknown>, dropKey: string): Record<string, unknown> {
  const next: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(map)) {
    if (key !== dropKey) next[key] = value;
  }
  return next;
}

interface EnrichmentEditsState {
  /** Persisted overrides mirrored locally, keyed `scopeId::productId::fieldKey`. Values are in the attribute's own shape (string, or a parsed object/array for `json` fields). */
  overrides: Record<string, unknown>;

  /** Records a persisted override so the edited cell reflects it without a refetch. */
  setOverride: (scopeId: string, productId: string, fieldKey: string, value: unknown) => void;
  /** Drops the override for one field, reverting the cell to the model output. */
  resetField: (scopeId: string, productId: string, fieldKey: string) => void;
  /** Forgets every override in a scope. */
  clearScope: (scopeId: string) => void;
}

export const useEnrichmentEditsStore = create<EnrichmentEditsState>()((set) => ({
  overrides: {},

  setOverride: (scopeId, productId, fieldKey, value) => {
    const key = editKey(scopeId, productId, fieldKey);
    set((state) => ({ overrides: { ...state.overrides, [key]: value } }));
  },

  resetField: (scopeId, productId, fieldKey) => {
    const key = editKey(scopeId, productId, fieldKey);
    set((state) => {
      if (!(key in state.overrides)) return state;
      return { overrides: withoutKey(state.overrides, key) };
    });
  },

  clearScope: (scopeId) => {
    set((state) => ({ overrides: withoutScope(state.overrides, scopeId) }));
  },
}));

/** Effective override for one field, or `undefined` when the operator hasn't edited it. */
export function selectEditedValue(
  state: Pick<EnrichmentEditsState, 'overrides'>,
  scopeId: string,
  productId: string,
  fieldKey: string,
): unknown {
  return state.overrides[editKey(scopeId, productId, fieldKey)];
}
